import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { RemoteContent, Title, formatDateTime, formatHours } from '@jupi/ui'
import { useAircraftMessages, type AircraftMessage } from '../api/queries'

const CLASS_LABEL: Record<number, string> = {
  1: 'aircraftMessage.class1',
  2: 'aircraftMessage.class2',
  4: 'aircraftMessage.class4',
}

export default function AircraftMessagesPage() {
  const { t } = useTranslation()
  const { data: messages = [], isLoading, error } = useAircraftMessages()

  return (
    <Box>
      <Title label={t('aircraftMessage.titlePlural')} />
      <RemoteContent isLoading={isLoading} error={error}>
        {messages.length === 0 && !isLoading ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {t('aircraftMessage.none')}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {messages.map((aircraftMessage) => (
              <MessageCard key={aircraftMessage.messageId} aircraftMessage={aircraftMessage} />
            ))}
          </Stack>
        )}
      </RemoteContent>
    </Box>
  )
}

function MessageCard({ aircraftMessage }: { aircraftMessage: AircraftMessage }) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {aircraftMessage.registration}
          </Typography>
          <Chip
            size="small"
            label={t(CLASS_LABEL[aircraftMessage.messageClass] ?? 'aircraftMessage.title')}
            color={aircraftMessage.messageClass === 4 ? 'primary' : 'default'}
          />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {formatDateTime(aircraftMessage.submittedAt)}
          </Typography>
        </Stack>

        <Typography variant="body2" sx={{ mb: 1 }}>
          {aircraftMessage.message}
        </Typography>

        {aircraftMessage.airframeHours !== null ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('aircraftMessage.airframeHours')}: {formatHours(aircraftMessage.airframeHours)}
            {aircraftMessage.nextMaintenanceHours !== null
              ? ` · ${t('aircraftMessage.nextMaintenanceHours')}: ${formatHours(aircraftMessage.nextMaintenanceHours)}`
              : ''}
            {aircraftMessage.nextMaintenanceDesc ? ` (${aircraftMessage.nextMaintenanceDesc})` : ''}
          </Typography>
        ) : null}

        {aircraftMessage.submittedByName ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('aircraftMessage.submittedBy')}: {aircraftMessage.submittedByName}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  )
}
